import type { BenefitSubscriptionRegistry } from "./benefitSubscriptions"
import type { ObservedResource, RefreshClock } from "./resourceRefreshCoordinator"

export interface BenefitRefreshEnvironment {
  visible(): boolean
  watch(listener: () => void): () => void
}
export const browserBenefitRefreshEnvironment: BenefitRefreshEnvironment = {
  visible: () => document.visibilityState === "visible",
  watch(listener) {
    window.addEventListener("focus", listener)
    window.addEventListener("online", listener)
    document.addEventListener("visibilitychange", listener)
    return () => {
      window.removeEventListener("focus", listener)
      window.removeEventListener("online", listener)
      document.removeEventListener("visibilitychange", listener)
    }
  },
}
const defaultClock: RefreshClock = {
  now: () => Date.now(),
  schedule(callback, ms) { const timer = setTimeout(callback, ms); return () => clearTimeout(timer) },
}

/** Trigger routing only. Reads, generations, dedupe and trailing work belong to the one
 * resource coordinator. No saved data, socket, authentication or command ownership here. */
export function startBenefitBackgroundRefresh(ports: {
  subscriptions: BenefitSubscriptionRegistry
  resources(): Iterable<ObservedResource>
  dirty(resource: ObservedResource, eventId?: string): boolean
  visible(value: boolean): void
  independentReadsChanged(): void
  scopeChanged(): void
}, options: { environment: BenefitRefreshEnvironment; clock?: RefreshClock; random?: () => number }) {
  const clock = options.clock ?? defaultClock
  let stopped = false, cancelPoll: (() => void) | undefined
  const releases: (() => void)[] = []
  function all(eventId?: string) {
    let accepted = false
    for (const resource of ports.resources()) accepted = ports.dirty(resource, eventId) || accepted
    if (accepted) ports.independentReadsChanged()
  }
  function schedule() {
    if (stopped || cancelPoll || !options.environment.visible()) return
    const jitter = Math.max(0, Math.min(1, (options.random ?? Math.random)()))
    cancelPoll = clock.schedule(() => {
      cancelPoll = undefined
      if (!stopped && options.environment.visible()) { all(); schedule() }
    }, 30_000 + Math.floor(jitter * 30_000))
  }
  ports.visible(options.environment.visible())
  releases.push(options.environment.watch(() => {
    if (stopped) return
    const visible = options.environment.visible()
    ports.visible(visible)
    if (!visible) { cancelPoll?.(); cancelPoll = undefined; return }
    all(); schedule()
  }))
  releases.push(ports.subscriptions.watchHint(hint => {
    if (stopped) return
    if ("resource" in hint) {
      for (const resource of ports.resources()) {
        if (resource.id.toLowerCase() === hint.resource.id.toLowerCase()) ports.dirty(resource, hint.eventId)
      }
    } else all(hint.eventId)
  }))
  for (const topic of ["applications", "catalogs", "people"] as const) {
    try {
      releases.push(ports.subscriptions.observe({ kind: "benefit-collection", topic }, state => {
        if (stopped) return
        if (state === "joined") all()
        if (state === "scopeChanged") ports.scopeChanged()
        // A missing collection permission cannot grant/deny record authority. Its
        // hints are unavailable; safe-read fallback still covers authorized records.
      }))
    } catch {
      // Bounded registry cap: do not allocate or retry endlessly. Poll remains safe.
    }
  }
  schedule()
  return () => {
    stopped = true; cancelPoll?.()
    for (const release of releases) release()
  }
}
